import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect } from 'react';

import {
  boardSwitcherSharedState,
  keyboardNavSharedState,
} from '@trello/board-switcher';
import { WorkspaceIdProvider } from '@trello/id-context';
import { useSharedState, useSharedStateSelector } from '@trello/shared-state';

import { CreateBoardListTile } from 'app/src/components/BoardTile/CreateBoardListTile';
import { CreateBoardTileLarge } from 'app/src/components/BoardTile/CreateBoardTileLarge';
import { focusBoardSwitcherNavItem } from './focusBoardSwitcherNavItem';
import { getBoardSwitcherSource } from './getBoardSwitcherSource';
import { useSetActiveBoardTileOnHoverOrFocus } from './useSetActiveBoardTileOnHoverOrFocus';

import * as styles from './CreateBoardTile.module.less';

interface CreateBoardTileProps {
  workspaceId: string;
}

export const CreateBoardTile: FunctionComponent<CreateBoardTileProps> = ({
  workspaceId,
}) => {
  const [{ activeId, activeSection }] = useSharedState(keyboardNavSharedState);
  const layout = useSharedStateSelector(
    boardSwitcherSharedState,
    useCallback((value) => value.layout, []),
  );
  const activeStateHandlers = useSetActiveBoardTileOnHoverOrFocus(
    'workspace',
    'create',
  );

  const isActive = activeId === 'create' && activeSection === 'workspace';
  const TileComponent =
    layout === 'list' ? CreateBoardListTile : CreateBoardTileLarge;

  useEffect(() => {
    if (isActive) {
      focusBoardSwitcherNavItem('workspace', 'create');
    }
  }, [isActive]);

  return (
    <WorkspaceIdProvider value={workspaceId}>
      <TileComponent
        className={cx(styles.createBoardTile, {
          [styles.grid]: layout === 'grid',
          [styles.active]: isActive,
        })}
        data-nav-item="board-tile"
        data-section="workspace"
        data-id="create"
        {...activeStateHandlers}
        analyticsSource={getBoardSwitcherSource()}
      />
    </WorkspaceIdProvider>
  );
};
